
/// <amd-module name="ArtifactPolicy/Templates/PolicyDefinitionTemplatesPanel" />
import React = require("react");

import { IVssComponentProperties, VssComponent, IVssComponentContext, Components } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Panel } from "VSSUI/Panel";
import { Header, TitleSize } from "VSSUI/Header";
import { Observer } from "VSSUI/Observer";
import { IObservableValue, ObservableValue, IObservableArray, ObservableArray } from "VSS/Core/Observable";

import * as Resources from "ArtifactPolicy/Templates/Resources";
import { TemplateSelectionTable } from "ArtifactPolicy/Templates/TemplateSelectionTable";
import { TemplateInputs } from "ArtifactPolicy/Templates/TemplateInputs";
import { constructPolicyDefinition, basicPolicyTemplatecontributionId } from "ArtifactPolicy/Templates/PolicyDefinition";

interface IProps extends IVssComponentProperties {
    title?: string;
    onDismiss: () => void;
    updateTemplateContent: (policyContent: string) => void;
}

export class PolicyDefinitionTemplatesPanel extends VssComponent<IProps>{
    public constructor(props: IProps, context: IVssComponentContext) {
        super(props, context);
        this._viewType = new ObservableValue<ViewType>(ViewType.TemplateSelection);
        this._selectedTemplateIds = new ObservableArray<string>([]);
        this._inputsObservableDictionary = {};
    }

    public render(): JSX.Element {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const templateContributionIds = contributionService.getContributionChildren("ms.vss-pipelinechecks.artifact-policy-definition-templates", "ms.vss-pipelinechecks.artifact-policy-definition-template");
        return (
            <Observer viewType={this._viewType} selectedTemplateIds={this._selectedTemplateIds} inputsInvalid={this._inputsInvalid}>
                {(observedProps: { viewType: ViewType, selectedTemplateIds: string[], inputsInvalid: boolean }) => {
                    let content: React.ReactNode;
                    switch (observedProps.viewType) {
                        case ViewType.TemplateSelection:
                            content = <TemplateSelectionTable
                                contributionIds={templateContributionIds || []}
                                selectedContributionsObservable={this._selectedTemplateIds} />;
                            break;
                        case ViewType.Inputs:
                            content = <TemplateInputs
                                selectedContributionIds={observedProps.selectedTemplateIds}
                                inputsObservableDictionary={this._inputsObservableDictionary}
                                inputsUpdatedCallback={this._inputsUpdated} />;
                            break;
                    }

                    return <Panel
                        onDismiss={this.props.onDismiss}
                        titleProps={{ text: this.props.title || Resources.ConfigurePolicyTitle, size: TitleSize.Large }}
                        footerButtonProps={[
                            ...(observedProps.viewType === ViewType.TemplateSelection ? [] : [{
                                id: "artifact-policy-templates-back-button",
                                text: Resources.Back,
                                onClick: this._onBackClick
                            }]),
                            ...[{
                                id: "artifact-policy-templates-next-button",
                                text: Resources.Next,
                                primary: true,
                                onClick: this._onNextClick,
                                disabled: this._isNextDisabled()
                            }]
                        ]}
                    >
                        {content}
                    </Panel>;
                }}
            </Observer>
        );
    }

    private _onBackClick = () => {
        if (this._viewType.value === ViewType.Inputs) {
            this._viewType.value = ViewType.TemplateSelection;
        }
    }

    private _onNextClick = () => {
        switch (this._viewType.value) {
            case ViewType.TemplateSelection:
                if (this._selectedTemplateIds.value.length !== 1 || this._selectedTemplateIds.value[0] !== basicPolicyTemplatecontributionId) {
                    this._viewType.value = ViewType.Inputs;
                    break;
                }
            case ViewType.Inputs:
                this.props.updateTemplateContent(constructPolicyDefinition(this.context.pageContext, this._selectedTemplateIds.value, this._inputsObservableDictionary));
                this.props.onDismiss();
                break;
        }
    }

    private _inputsUpdated = () => {
        const isInvalid = Object.keys(this._inputsObservableDictionary).some(key => !this._inputsObservableDictionary[key].value);
        if (this._inputsInvalid.value !== isInvalid) {
            this._inputsInvalid.value = isInvalid;
        }
    }

    private _isNextDisabled(): boolean {
        if (this._viewType.value === ViewType.TemplateSelection && this._selectedTemplateIds.value.length === 0) {
            return true;
        }
        else if (this._viewType.value === ViewType.Inputs) {
            return this._inputsInvalid.value;
        }

        return false;
    }

    private _viewType: IObservableValue<ViewType>;
    private _selectedTemplateIds: IObservableArray<string>;
    private _inputsInvalid: IObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _inputsObservableDictionary: { [key: string]: IObservableValue<string> };
}

enum ViewType {
    TemplateSelection,
    Inputs
}

Components.add("PolicyDefinitionTemplatesPanel", PolicyDefinitionTemplatesPanel);
